import React, {useState} from "react";
import {Box, Button, Chip, CircularProgress, Collapse, Divider, IconButton, Stack, Switch, TextField, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ChevronRightIcon, Delete02Icon, FloppyDiskIcon} from "@hugeicons/core-free-icons";
import {green, grey, red} from "@mui/material/colors";
import {toast} from "react-toastify";
import {AmiRepository} from "../../../../repositories/AmiRepository";
import useConfirm from "../../../../hooks/useConfirm";
import {varsListToObject, varsObjectToList, VariablesEditor} from "./VariablesEditor";
import {DEFAULT_CONTEXT, DEFAULT_TRUNK, ExtensionAdvancedSettings} from "./ExtensionAdvancedSettings";
import {getTarget, SubProjectFields, toVariableRows, toVariables} from "./SubProjectFields";
import {subProjectLabel} from "../../../../utils/subProjects";

const statusColor = (enabled) => {
  if (typeof enabled !== "boolean") return grey[400];
  return enabled ? green[500] : red[500];
};

// subProjects (registry projects): the extension's sub project and version are selected above the
// variable list and saved as variables that the list does not show
export const ExtensionCard = ({extension, flowUid: pageFlowUid, subProjects, onSaved, onDeleted}) => {
  // with several sub projects on one page, each extension is managed through its own flow
  const flowUid = extension.flow_uid || pageFlowUid;
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingRedirect, setSavingRedirect] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [name, setName] = useState(extension.name || "");
  const [description, setDescription] = useState(extension.description || "");
  const [trunk, setTrunk] = useState(extension.trunk || DEFAULT_TRUNK);
  const [context, setContext] = useState(extension.context || DEFAULT_CONTEXT);
  const [varRows, setVarRows] = useState(() => subProjects
    ? toVariableRows(extension.variables)
    : varsObjectToList(extension.variables));
  const [target, setTarget] = useState(() => subProjects ? getTarget(extension, subProjects) : null);
  const subProject = subProjects?.find(item => item.id === getTarget(extension, subProjects).subProjectId);
  const [redirectContext, setRedirectContext] = useState(extension.redirect_context || "");
  const [redirectExten, setRedirectExten] = useState(extension.redirect_exten || "");

  const handleToggle = () => {
    const enable = !extension.enabled;
    setToggling(true);
    const request = enable
      ? AmiRepository.enableExtension(flowUid, extension.extension)
      : AmiRepository.disableExtension(flowUid, extension.extension);

    request
      .then((res) => {
        toast.success(`Extension ${extension.extension} ${enable ? "enabled" : "disabled"}`);
        if (res?.data?.astdb_synced === false) {
          toast.warning("Saved, but the live sync to Asterisk failed");
        }
        onSaved({...extension, ...res?.data});
      })
      .catch((err) => toast.error(err?.response?.data?.error || `Failed to ${enable ? "enable" : "disable"} extension`))
      .finally(() => setToggling(false));
  };

  const handleDelete = async () => {
    if (!extension.id) {
      toast.error("This extension has no internal id and can't be deleted");
      return;
    }
    const confirmed = await confirm({
      title: "Delete Extension",
      message: `Are you sure you want to delete extension ${extension.extension}? This removes it from the dialplan entirely and cannot be undone.`,
    });
    if (!confirmed) return;
    setDeleting(true);
    AmiRepository.deleteExtensionById(flowUid, extension.id)
      .then(() => {
        toast.success(`Extension ${extension.extension} deleted`);
        onDeleted?.(extension);
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Failed to delete extension"))
      .finally(() => setDeleting(false));
  };

  const handleSaveVars = () => {
    setSaving(true);
    AmiRepository.registerExtension(flowUid, {
      extension: extension.extension,
      flow_uid: extension.flow_uid,
      name: name.trim() || undefined,
      description,
      trunk: trunk.trim() || DEFAULT_TRUNK,
      context: context.trim() || DEFAULT_CONTEXT,
      variables: subProjects
        ? toVariables(varRows, target, subProjects, extension.variables)
        : varsListToObject(varRows),
    })
      .then((res) => {
        toast.success(`Extension ${extension.extension} updated`);
        if (res?.data?.conf_regenerated === false) {
          toast.error(res?.data?.conf_error || "Configuration file failed to regenerate");
        }
        onSaved({...extension, ...res?.data});
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Failed to update extension"))
      .finally(() => setSaving(false));
  };

  const handleSaveRedirect = () => {
    if (!redirectContext.trim() || !redirectExten.trim()) {
      toast.error("Both redirect context and extension are required");
      return;
    }
    setSavingRedirect(true);
    AmiRepository.redirectExtension(flowUid, extension.extension, {
      context: redirectContext.trim(),
      exten: redirectExten.trim(),
    })
      .then((res) => {
        toast.success(`Redirect target set for ${extension.extension}`);
        if (res?.data?.astdb_synced === false) {
          toast.warning("Saved, but the live sync to Asterisk failed");
        }
        onSaved({...extension, ...res?.data});
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Failed to set redirect target"))
      .finally(() => setSavingRedirect(false));
  };

  return (
    <Box className={"border-shadow"} sx={{borderRadius: 2, p: 2}}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Stack direction="row" alignItems="center" gap={1.25} sx={{flex: 1, minWidth: 0}}>
          <Tooltip title={typeof extension.enabled !== "boolean" ? "Status unknown" : extension.enabled ? "Active" : "Not active"}>
            <Box sx={{width: 9, height: 9, borderRadius: "50%", bgcolor: statusColor(extension.enabled), flexShrink: 0}}/>
          </Tooltip>
          <Typography fontFamily="monospace" fontWeight={700} fontSize={14}>{extension.extension}</Typography>
          {extension.name && (
            <Typography fontSize={13.5} fontWeight={600} noWrap sx={{minWidth: 0}}>{extension.name}</Typography>
          )}
          {extension.description && (
            <Typography fontSize={12.5} color="text.secondary" noWrap sx={{minWidth: 0}}>
              {extension.description}
            </Typography>
          )}
          {subProject && (
            <Chip size="small" label={subProjectLabel(subProject)} sx={{bgcolor: grey[100], flexShrink: 0}}/>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
          {toggling ? (
            <CircularProgress size={16} sx={{mx: 1.5}}/>
          ) : (
            <Tooltip title={extension.enabled ? "Disable extension" : "Enable extension"}>
              <Switch size="small" checked={!!extension.enabled} onChange={handleToggle}/>
            </Tooltip>
          )}
          {deleting ? (
            <CircularProgress size={16} sx={{mx: 0.75}}/>
          ) : (
            <Tooltip title={extension.id ? "Delete extension" : "No internal id - can't be deleted"}>
              <span>
                <IconButton size="small" color="error" onClick={handleDelete} disabled={!extension.id}>
                  <HugeiconsIcon icon={Delete02Icon} size={16}/>
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton size="small" onClick={() => setOpen(v => !v)}>
            <HugeiconsIcon icon={ChevronRightIcon} size={16} style={{transition: "transform 0.15s", transform: open ? "rotate(90deg)" : "rotate(0deg)"}}/>
          </IconButton>
        </Stack>
      </Stack>

      <Collapse in={open}>
        <Divider sx={{my: 1.5}}/>
        <Stack gap={2}>
          {extension.flow_uid && (
            <Typography fontSize={11} color="text.secondary" fontFamily="monospace">
              flow_uid: {extension.flow_uid}
            </Typography>
          )}

          <TextField
            label="Name" size="small" fullWidth
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Support bot"
          />

          <TextField
            label="Description" size="small" fullWidth
            value={description} onChange={(e) => setDescription(e.target.value)}
          />

          <ExtensionAdvancedSettings
            trunk={trunk} context={context}
            onTrunkChange={setTrunk} onContextChange={setContext}
          />
          {subProjects && (
            <SubProjectFields subProjects={subProjects} target={target} onChange={setTarget}/>
          )}
          <VariablesEditor rows={varRows} onChange={setVarRows}/>


          <Box sx={{ml: "auto"}}>
            <Button
              size="small" variant="modal" color="secondary"
              startIcon={saving ? <CircularProgress size={13} color="inherit"/> : <HugeiconsIcon icon={FloppyDiskIcon} size={14}/>}
              onClick={handleSaveVars}
              disabled={saving}
              sx={{textTransform: "none", borderRadius: 2, fontSize: 12}}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </Box>

          {!extension.enabled && (
            <>
              <Divider/>
              <Box>
                <Typography fontSize={11} fontWeight={700} textTransform="uppercase" letterSpacing={0.5} color="text.secondary" mb={0.75}>
                  Redirect Target (while disabled)
                </Typography>
                <Stack direction="row" gap={1}>
                  <TextField
                    size="small" label="Context" placeholder="e.g. audiosocket-transfer"
                    value={redirectContext} onChange={(e) => setRedirectContext(e.target.value)} sx={{flex: 1}}
                  />
                  <TextField
                    size="small" label="Extension" placeholder="e.g. 963"
                    value={redirectExten} onChange={(e) => setRedirectExten(e.target.value)} sx={{flex: 1}}
                  />
                </Stack>
                <Box sx={{ml: "auto", mt: 1, display: "flex"}}>
                  <Button
                    size="small" variant="ghost" color="secondary" sx={{ml: "auto", textTransform: "none", fontSize: 12}}
                    onClick={handleSaveRedirect}
                    disabled={savingRedirect}
                    startIcon={savingRedirect ? <CircularProgress size={13}/> : null}
                  >
                    Save Redirect
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};
